import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Bot, Plus, Trash2, X, Loader2, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAINS = ["market", "academic", "business", "creative", "technical", "universal"];
const PHASES = ["discovery", "deepdive", "verify", "synthesis", "editorial"];

/** Custom user agents manager. Users can define specialised agents that will be
 *  included in their own Deep Research swarms (max 20 per user).
 */
export const CustomAgentsModal = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [agents, setAgents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    role: "",
    domain: "market",
    phase: "deepdive",
    system_prompt: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/deep-research/agents/custom`);
      setAgents(r.data.agents || []);
    } catch {
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const create = async () => {
    if (form.name.length < 2 || form.system_prompt.length < 20) {
      toast.error(t("custom_agent_validation"));
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/deep-research/agents/custom`, form);
      toast.success(t("custom_agent_created"));
      setForm({ name: "", role: "", domain: "market", phase: "deepdive", system_prompt: "" });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("custom_agent_error"));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!confirm(t("custom_agent_delete_confirm"))) return;
    try {
      await axios.delete(`${API}/deep-research/agents/custom/${id}`);
      load();
    } catch {
      toast.error(t("custom_agent_error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="custom-agents-modal"
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/30 border-purple-500/30 p-0"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-zinc-800/60 hover:bg-zinc-700 text-zinc-300"
        >
          <X size={16} />
        </button>

        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Bot size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">{t("custom_agents_title")}</h2>
              <p className="text-xs text-zinc-400">{t("custom_agents_desc")}</p>
            </div>
            {!showForm && (
              <Button
                data-testid="custom-agent-new-btn"
                onClick={() => setShowForm(true)}
                size="sm"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
              >
                <Plus size={14} className="mr-1" /> {t("custom_agent_new")}
              </Button>
            )}
          </div>
          <div className="text-[10px] text-zinc-500">
            {agents?.length || 0} / 20 {t("custom_agents_slot")}
          </div>
        </div>

        <div className="p-6">
          {showForm && (
            <div className="rounded-xl border border-purple-500/40 bg-purple-500/5 p-4 mb-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("custom_agent_name")}>
                  <Input
                    data-testid="custom-agent-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ör: Turizm Analisti"
                    className="bg-zinc-800 border-zinc-700 text-zinc-100"
                    maxLength={80}
                  />
                </Field>
                <Field label={t("custom_agent_role")}>
                  <Input
                    data-testid="custom-agent-role-input"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="analyst / writer / researcher"
                    className="bg-zinc-800 border-zinc-700 text-zinc-100"
                    maxLength={40}
                  />
                </Field>
                <Field label={t("custom_agent_domain")}>
                  <Select value={form.domain} onValueChange={(v) => setForm({ ...form, domain: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="custom-agent-domain-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOMAINS.map((d) => (
                        <SelectItem key={d} value={d}>{t(`dr_domain_${d}`) !== `dr_domain_${d}` ? t(`dr_domain_${d}`) : d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("custom_agent_phase")}>
                  <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="custom-agent-phase-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PHASES.map((p, i) => (
                        <SelectItem key={p} value={p}>{i + 1}. {t(`dr_phase_${p}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label={t("custom_agent_prompt")}>
                <Textarea
                  data-testid="custom-agent-prompt-input"
                  value={form.system_prompt}
                  onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
                  placeholder="Sen bir uzman turist rehberisin. Coğrafi bölgeler, otel önerileri..."
                  className="min-h-[100px] bg-zinc-800 border-zinc-700 text-zinc-100"
                  maxLength={2000}
                />
              </Field>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowForm(false)} className="border-zinc-700 text-zinc-300">
                  {t("cancel")}
                </Button>
                <Button
                  data-testid="custom-agent-save-btn"
                  size="sm"
                  onClick={create}
                  disabled={creating}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                >
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <><Sparkles size={12} className="mr-1" /> {t("custom_agent_save")}</>}
                </Button>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-purple-400" /></div>
          )}

          {!loading && agents?.length === 0 && !showForm && (
            <div className="text-center py-12 text-zinc-500">
              <Bot size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">{t("custom_agents_empty")}</p>
              <p className="text-xs mt-1 text-zinc-600">{t("custom_agents_empty_hint")}</p>
            </div>
          )}

          {agents?.map((a) => (
            <div
              key={a.id}
              data-testid={`custom-agent-${a.id}`}
              className="flex items-start gap-3 p-3 mb-2 rounded-lg border border-zinc-800 bg-zinc-800/30"
            >
              <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bot size={14} className="text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white">{a.name}</span>
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{a.role}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">{a.domain}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{a.phase}</span>
                </div>
                <p className="text-xs text-zinc-400 mt-1 line-clamp-2 leading-snug">{a.system_prompt}</p>
              </div>
              <button
                onClick={() => remove(a.id)}
                data-testid={`custom-agent-delete-${a.id}`}
                className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-zinc-400 mb-1 block">{label}</Label>
    {children}
  </div>
);

export default CustomAgentsModal;
